import { CollectionProduct } from '../models/collection.interface';
import { useProductsBySkus } from './useProductsBySkus';

/**
 * Filters collection products to only show items that are valid products
 * Matches against all variants in a product, not just the main SKU
 */
export const useFilteredCollectionProducts = (products: CollectionProduct[] = []) => {
  const productSkus = products.map((p) => p.sku);

  const { validProducts } = useProductsBySkus(productSkus, true);

  // Create a Set of all valid variant SKUs from all products
  const validVariantSkus = new Set<string>();
  validProducts.forEach((product) => {
    if (!product) {
      return;
    }

    // Add the main product SKU
    if (product.sku) {
      validVariantSkus.add(product.sku);
    }

    // Add all variant SKUs
    const allVariants = [product.masterVariant, ...(product.variants || [])];
    allVariants.forEach((variant) => {
      if (variant?.sku) {
        validVariantSkus.add(variant.sku);
      }
    });
  });

  // Create a map of valid SKU to ecommName
  const skuToEcommNameMap = new Map<string, string>();
  validProducts.forEach((product) => {
    if (!product) {
      return;
    }

    if (product.sku) {
      skuToEcommNameMap.set(product.sku, product.ecommName);
    }

    const allVariants = [product.masterVariant, ...(product.variants || [])];
    allVariants.forEach((variant) => {
      if (variant?.sku) {
        skuToEcommNameMap.set(variant.sku, variant.ecommName);
      }
    });
  });

  // Filter collection products to only include those whose SKU matches a valid variant
  // and enrich with ecommName
  const filteredProducts = products
    .filter((p) => validVariantSkus.has(p.sku))
    .map((p) => ({
      ...p,
      ecommName: skuToEcommNameMap.get(p.sku),
    }));

  return filteredProducts;
};
